import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { apiClient } from "../../api/apiServices";

const DEBOUNCE_MS = 800;
const Sk = ({ className = "" }) => <div className={`animate-pulse bg-skeleton rounded-xl ${className}`} />;

export default function SavedBanksPage() {
  const navigate = useNavigate();

  const [savedBanks,   setSavedBanks]   = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [showAddForm,  setShowAddForm]  = useState(false);
  const [deletingId,   setDeletingId]   = useState(null);

  const [banks,         setBanks]         = useState([]);
  const [banksLoading,  setBanksLoading]  = useState(false);
  const [bankSearch,    setBankSearch]    = useState("");
  const [showBankList,  setShowBankList]  = useState(false);
  const [selectedBank,  setSelectedBank]  = useState(null);
  const [accountNumber, setAccountNumber] = useState("");
  const [accountName,   setAccountName]   = useState("");
  const [verifying,     setVerifying]     = useState(false);
  const [verified,      setVerified]      = useState(false);
  const [saving,        setSaving]        = useState(false);

  const debounceRef = useRef(null);

  useEffect(() => {
    apiClient.get("/mtrade/banks/saved")
      .then(r => { if (r.data.status) setSavedBanks(r.data.data || []); })
      .catch(() => toast.error("Failed to load saved banks"))
      .finally(() => setLoading(false));
  }, []);

  const loadBanks = useCallback(async () => {
    if (banks.length > 0) return;
    setBanksLoading(true);
    try {
      const r = await apiClient.get("/mtrade/banks");
      if (r.data.status) setBanks(r.data.banks);
    } catch {} finally { setBanksLoading(false); }
  }, [banks.length]);

  const handleOpenAdd = () => {
    setShowAddForm(true);
    loadBanks();
  };

  const handleCloseAdd = () => {
    setShowAddForm(false);
    setSelectedBank(null);
    setAccountNumber("");
    setAccountName("");
    setVerified(false);
    setBankSearch("");
  };

  const verifyAccount = useCallback(async (bank, accNum) => {
    if (!bank || accNum.length !== 10) return;
    setVerifying(true); setAccountName(""); setVerified(false);
    try {
      const r = await apiClient.post("/mtrade/banks/verify", { accountNumber: accNum, bankCode: bank.code });
      if (r.data.status) {
        setAccountName(r.data.data.account_name);
        setVerified(true);
      } else {
        toast.error(r.data.message || "Account not found");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Verification failed");
    } finally { setVerifying(false); }
  }, []);

  const handleAccountChange = (val) => {
    setAccountNumber(val); setAccountName(""); setVerified(false);
    clearTimeout(debounceRef.current);
    if (val.length === 10 && selectedBank) {
      debounceRef.current = setTimeout(() => verifyAccount(selectedBank, val), DEBOUNCE_MS);
    }
  };

  const handleBankSelect = (bank) => {
    setSelectedBank(bank); setShowBankList(false); setBankSearch("");
    setAccountName(""); setVerified(false);
    if (accountNumber.length === 10) setTimeout(() => verifyAccount(bank, accountNumber), 300);
  };

  const handleSave = async () => {
    if (!verified || !selectedBank) return;
    if (savedBanks.find(b => b.accountNumber === accountNumber)) {
      toast.warn("This account is already saved");
      return;
    }
    setSaving(true);
    try {
      const r = await apiClient.post("/mtrade/banks/saved", {
        accountNumber,
        bankCode: selectedBank.code,
        bankName: selectedBank.name,
      });
      if (r.data.status) {
        setSavedBanks(prev => [...prev, r.data.data]);
        toast.success("Bank account saved!");
        handleCloseAdd();
      } else {
        toast.error(r.data.message || "Failed to save bank");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save bank");
    } finally { setSaving(false); }
  };

  const handleDelete = async (accNum) => {
    setDeletingId(accNum);
    try {
      await apiClient.delete(`/mtrade/banks/saved/${accNum}`);
      setSavedBanks(prev => prev.filter(b => b.accountNumber !== accNum));
      toast.success("Bank removed");
    } catch {
      toast.error("Failed to remove bank");
    } finally { setDeletingId(null); }
  };

  const filteredBanks = banks.filter(b => b.name.toLowerCase().includes(bankSearch.toLowerCase()));

  return (
    <div className="min-h-screen bg-bg flex flex-col">
      <div className="bg-surface px-5 pt-12 pb-4 border-b border-border flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-xl bg-surface-hover flex items-center justify-center">
              <i className="ri-arrow-left-line text-secondary text-lg" />
            </button>
            <div>
              <h1 className="text-primary font-bold text-lg font-display">Saved Banks</h1>
              <p className="text-muted text-xs">Manage your withdrawal accounts</p>
            </div>
          </div>
          {!showAddForm && (
            <button onClick={handleOpenAdd}
              className="flex items-center gap-1.5 px-3.5 py-2 bg-brand text-on-brand text-xs font-bold rounded-xl hover:bg-brand-hover transition-colors active:scale-95">
              <i className="ri-add-line" />Add Bank
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 pt-5 pb-10 space-y-5">

        {showAddForm && (
          <div className="bg-surface rounded-2xl border border-border shadow-card p-5 space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-primary font-bold text-sm">Add New Bank Account</p>
              <button onClick={handleCloseAdd} className="w-8 h-8 bg-surface-hover rounded-xl flex items-center justify-center hover:opacity-80 transition-colors">
                <i className="ri-close-line text-secondary" />
              </button>
            </div>

            <div>
              <p className="text-secondary text-xs font-semibold mb-2">Bank</p>
              <button onClick={() => setShowBankList(true)}
                className="w-full flex items-center justify-between bg-surface-muted border-2 border-border-strong rounded-2xl px-4 py-3.5 hover:border-brand transition-colors">
                <span className="text-sm font-medium" style={{ color: selectedBank ? "var(--color-text-primary)" : "var(--color-text-faint)" }}>
                  {selectedBank ? selectedBank.name : "Select your bank"}
                </span>
                <i className="ri-arrow-down-s-line text-muted" />
              </button>
            </div>

            <div>
              <p className="text-secondary text-xs font-semibold mb-2">Account Number</p>
              <div className="bg-surface-muted border-2 rounded-2xl flex items-center px-4 transition-colors"
                style={{ borderColor: verified ? "var(--color-brand)" : "var(--color-border-strong)" }}>
                <input
                  type="tel" inputMode="numeric" maxLength={10}
                  value={accountNumber}
                  onChange={e => handleAccountChange(e.target.value.replace(/\D/g, ""))}
                  placeholder="10-digit account number"
                  className="flex-1 py-3.5 outline-none text-primary text-sm bg-transparent placeholder:text-faint font-mono"
                />
                <div className="ml-2 flex-shrink-0 w-5 flex items-center justify-center">
                  {verifying
                    ? <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: "var(--color-brand)", borderTopColor: "transparent" }} />
                    : verified ? <i className="ri-checkbox-circle-fill text-lg" style={{ color: "var(--color-brand)" }} /> : null}
                </div>
              </div>
              {!selectedBank && accountNumber.length > 0 && (
                <p className="text-xs mt-1.5 px-1" style={{ color: "var(--color-warning)" }}>⚠ Select a bank first</p>
              )}
            </div>

            {accountName && (
              <div className="flex items-center gap-2 px-4 py-3 bg-success-light border border-success-border rounded-xl">
                <i className="ri-user-line text-sm" style={{ color: "var(--color-success)" }} />
                <p className="text-sm font-bold" style={{ color: "var(--color-success-text)" }}>{accountName}</p>
              </div>
            )}

            <button
              onClick={handleSave}
              disabled={!verified || saving}
              className="w-full py-3.5 bg-brand hover:bg-brand-hover disabled:bg-surface-hover disabled:text-faint text-on-brand font-bold text-sm rounded-2xl transition-all active:scale-95 flex items-center justify-center gap-2">
              {saving
                ? <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Saving…</>
                : !verified ? "Enter account number to continue"
                : <><i className="ri-save-line" />Save Bank Account</>}
            </button>
          </div>
        )}

        <div>
          {!showAddForm && (
            <p className="text-secondary text-xs font-semibold uppercase tracking-wider mb-3">
              Your Banks {!loading && `(${savedBanks.length})`}
            </p>
          )}

          {loading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-surface rounded-2xl border border-border shadow-card p-4 flex items-center gap-3">
                  <Sk className="w-12 h-12 rounded-xl flex-shrink-0" />
                  <div className="flex-1 space-y-2"><Sk className="h-3 w-36" /><Sk className="h-2.5 w-28" /></div>
                  <Sk className="w-8 h-8 rounded-xl" />
                </div>
              ))}
            </div>
          ) : savedBanks.length === 0 ? (
            <div className="bg-surface rounded-2xl border border-border shadow-card p-10 text-center">
              <div className="w-14 h-14 bg-surface-hover rounded-2xl flex items-center justify-center mx-auto mb-3">
                <i className="ri-bank-line text-muted text-2xl" />
              </div>
              <p className="text-secondary font-semibold text-sm mb-1">No saved banks yet</p>
              <p className="text-muted text-xs mb-4">Save a bank account for faster withdrawals</p>
              <button onClick={handleOpenAdd}
                className="px-6 py-2.5 bg-brand text-on-brand font-semibold rounded-xl text-sm hover:bg-brand-hover transition-colors">
                Add Your First Bank
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {savedBanks.map(bank => (
                <div key={bank.accountNumber}
                  className="bg-surface rounded-2xl border border-border shadow-card p-4 flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "var(--color-brand-light)", boxShadow: "inset 0 0 0 1px var(--color-brand-ring)" }}>
                    <i className="ri-bank-line text-xl" style={{ color: "var(--color-brand)" }} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-primary text-sm font-bold truncate">{bank.accountName}</p>
                    <p className="text-secondary text-xs font-mono">{bank.accountNumber}</p>
                    <p className="text-muted text-xs">{bank.bankName}</p>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => navigate("/withdraw-naira", { state: { preselected: bank } })}
                      className="w-8 h-8 rounded-xl flex items-center justify-center transition-colors"
                      style={{ background: "var(--color-brand-light)" }}
                      title="Withdraw to this account">
                      <i className="ri-arrow-up-circle-line text-sm" style={{ color: "var(--color-brand)" }} />
                    </button>
                    <button
                      onClick={() => handleDelete(bank.accountNumber)}
                      disabled={deletingId === bank.accountNumber}
                      className="w-8 h-8 bg-danger-light rounded-xl flex items-center justify-center transition-colors disabled:opacity-50">
                      {deletingId === bank.accountNumber
                        ? <div className="w-3.5 h-3.5 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: "var(--color-danger)", borderTopColor: "transparent" }} />
                        : <i className="ri-delete-bin-line text-sm" style={{ color: "var(--color-danger)" }} />}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-start gap-3 px-4 py-3 bg-info-light border border-info-border rounded-xl">
          <i className="ri-information-line text-sm flex-shrink-0 mt-0.5" style={{ color: "var(--color-info)" }} />
          <p className="text-xs leading-relaxed" style={{ color: "var(--color-info-text)" }}>
            Saved banks are verified with Paystack. Tap the <i className="ri-arrow-up-circle-line" /> button on any bank to start a withdrawal directly to that account.
          </p>
        </div>
      </div>

      {showBankList && (
        <div className="fixed inset-0 z-50 flex items-end" style={{ background: "var(--color-overlay)" }}
          onClick={() => setShowBankList(false)}>
          <div className="w-full bg-surface rounded-t-3xl max-h-[75vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-border">
              <p className="font-bold text-primary font-display">Select Bank</p>
              <button onClick={() => setShowBankList(false)} className="w-8 h-8 bg-surface-hover rounded-xl flex items-center justify-center">
                <i className="ri-close-line text-secondary" />
              </button>
            </div>
            <div className="px-4 py-3">
              <div className="flex items-center gap-2 bg-surface-hover rounded-xl px-3 py-2.5">
                <i className="ri-search-line text-muted text-sm" />
                <input type="text" value={bankSearch} onChange={e => setBankSearch(e.target.value)}
                  placeholder="Search banks…" autoFocus
                  className="flex-1 bg-transparent text-sm text-secondary outline-none placeholder:text-faint" />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-4 pb-6">
              {banksLoading ? (
                <div className="space-y-2">{[...Array(6)].map((_, i) => <div key={i} className="h-12 bg-skeleton rounded-xl animate-pulse" />)}</div>
              ) : filteredBanks.length === 0 ? (
                <p className="text-muted text-sm text-center py-8">No banks found</p>
              ) : (
                <div className="space-y-1">
                  {filteredBanks.map(bank => (
                    <button key={bank.code} onClick={() => handleBankSelect(bank)}
                      className="w-full flex items-center px-4 py-3.5 rounded-xl text-left transition-all"
                      style={{
                        background: selectedBank?.code === bank.code ? "var(--color-brand-light)" : "transparent",
                        border:     selectedBank?.code === bank.code ? "1px solid var(--color-brand-border)" : "none",
                      }}>
                      <div className="w-8 h-8 bg-surface-hover rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                        <i className="ri-bank-line text-muted text-sm" />
                      </div>
                      <span className="text-sm font-medium" style={{ color: selectedBank?.code === bank.code ? "var(--color-brand-text)" : "var(--color-text-secondary)" }}>
                        {bank.name}
                      </span>
                      {selectedBank?.code === bank.code && <i className="ri-check-line ml-auto" style={{ color: "var(--color-brand)" }} />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
